<template>
  <div class="notification-bell" ref="bellRef">
    <button class="bell-button" @click="toggleDropdown" :class="{ active: showDropdown }">
      <i class="pi pi-bell"></i>
      <span v-if="unreadCount > 0" class="badge">{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
    </button>

    <div v-if="showDropdown" class="dropdown">
      <div class="dropdown-header">
        <h4>Notifications</h4>
        <router-link to="/community-alerts" class="view-all" @click="showDropdown = false">
          View All
        </router-link>
      </div>

      <div v-if="loading" class="dropdown-loading">
        <div class="spinner-small"></div>
      </div>

      <div v-else-if="alerts.length === 0" class="dropdown-empty">
        <i class="pi pi-check-circle"></i>
        <p>No new alerts</p>
      </div>

      <div v-else class="dropdown-list">
        <div
          v-for="alert in alerts"
          :key="alert.id"
          class="dropdown-item"
          @click="viewAlert(alert.id)"
        >
          <span class="item-dot" :class="alert.severity"></span>
          <div class="item-content">
            <span class="item-title">{{ alert.disease_name }}</span>
            <span class="item-meta">
              <span class="item-severity" :class="alert.severity">{{ alert.severity }}</span>
              <span class="item-time">{{ formatTimeAgo(alert.created_at) }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { communityAlertService } from '@/services/communityAlertService'

export default {
  name: 'NotificationBell',
  setup() {
    const router = useRouter()
    const bellRef = ref(null)
    const showDropdown = ref(false)
    const loading = ref(false)
    const unreadCount = ref(0)
    const alerts = ref([])
    let pollInterval = null

    const fetchUnreadCount = async () => {
      try {
        unreadCount.value = await communityAlertService.getUnreadCount()
      } catch {
        unreadCount.value = 0
      }
    }

    const fetchAlerts = async () => {
      try {
        loading.value = true
        alerts.value = await communityAlertService.getDashboardAlerts(5)
      } catch {
        alerts.value = []
      } finally {
        loading.value = false
      }
    }

    const toggleDropdown = async () => {
      showDropdown.value = !showDropdown.value
      if (showDropdown.value) {
        await fetchAlerts()
      }
    }

    const viewAlert = (id) => {
      showDropdown.value = false
      router.push(`/community-alerts/${id}`)
    }

    const formatTimeAgo = (dateString) => {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      if (diffMins < 60) return `${diffMins}m ago`
      const diffHours = Math.floor(diffMins / 60)
      if (diffHours < 24) return `${diffHours}h ago`
      const diffDays = Math.floor(diffHours / 24)
      return `${diffDays}d ago`
    }

    const handleClickOutside = (event) => {
      if (bellRef.value && !bellRef.value.contains(event.target)) {
        showDropdown.value = false
      }
    }

    onMounted(() => {
      fetchUnreadCount()
      pollInterval = setInterval(fetchUnreadCount, 60000)
      document.addEventListener('click', handleClickOutside)
    })

    onUnmounted(() => {
      if (pollInterval) clearInterval(pollInterval)
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      bellRef, showDropdown, loading, unreadCount, alerts,
      toggleDropdown, viewAlert, formatTimeAgo
    }
  }
}
</script>

<style scoped>
.notification-bell {
  position: relative;
}

.bell-button {
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: #64748b;
  font-size: 1.125rem;
  border-radius: 8px;
  transition: all 0.2s;
}

.bell-button:hover,
.bell-button.active {
  background: #f1f5f9;
  color: #1e293b;
}

.badge {
  position: absolute;
  top: 2px;
  right: 2px;
  background: #ef4444;
  color: white;
  font-size: 0.625rem;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  line-height: 1;
  border: 2px solid white;
}

.dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 340px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
}

.dropdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f1f5f9;
}

.dropdown-header h4 {
  margin: 0;
  font-size: 0.875rem;
  color: #1e293b;
}

.view-all {
  font-size: 0.75rem;
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.view-all:hover { text-decoration: underline; }

.dropdown-loading {
  display: flex;
  justify-content: center;
  padding: 2rem;
}

.spinner-small {
  width: 24px;
  height: 24px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.dropdown-empty {
  padding: 2rem;
  text-align: center;
  color: #94a3b8;
}

.dropdown-empty i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  display: block;
  color: #10b981;
}

.dropdown-empty p {
  margin: 0;
  font-size: 0.8125rem;
}

.dropdown-list {
  max-height: 320px;
  overflow-y: auto;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid #f8fafc;
}

.dropdown-item:last-child { border-bottom: none; }

.dropdown-item:hover { background: #f8fafc; }

.item-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.item-dot.mild { background: #10b981; }
.item-dot.moderate { background: #f59e0b; }
.item-dot.severe { background: #f97316; }
.item-dot.critical { background: #ef4444; }

.item-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.item-title {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #1e293b;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
}

.item-severity {
  padding: 0.0625rem 0.375rem;
  border-radius: 9999px;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.625rem;
}

.item-severity.mild { background: #d1fae5; color: #065f46; }
.item-severity.moderate { background: #fef3c7; color: #92400e; }
.item-severity.severe { background: #fed7aa; color: #9a3412; }
.item-severity.critical { background: #fecaca; color: #991b1b; }

.item-time { color: #94a3b8; }

@media (max-width: 480px) {
  .dropdown {
    width: calc(100vw - 2rem);
    right: -0.5rem;
  }
}
</style>
